import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import {
  formatCurrency, formatDate, formatDaysUntil,
  jurisdictionLabel, urgencyLabel, transactionTypeLabel,
} from '../lib/format';
import { Loading, Empty, StatusPill } from '../components/Shared';
import { LearningCard } from '../components/LearningCard';

// ================================================================
// Entity Detail page
// ================================================================

export function EntityPage() {
  const [entity, setEntity] = useState<any>(null);

  useEffect(() => {
    api.entities.list().then((r) => setEntity(r.entities[0] ?? null));
  }, []);

  if (entity === null) return <Loading />;
  if (!entity) return <Empty title="Sin entidad" description="Completa el onboarding para comenzar." />;

  return (
    <div className="fade-up">
      <div className="page-header">
        <div className="page-eyebrow">Entidad</div>
        <h1 className="page-title"><em>{entity.legal_name}</em></h1>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
        <Section title="Registro">
          <Row label="Estado" value={entity.state} />
          <Row label="Tipo" value={entity.entity_type.toUpperCase()} />
          <Row label="Formación" value={formatDate(entity.formation_date)} />
          <Row label="Filing ID" value={entity.filing_id || '—'} mono />
        </Section>

        <Section title="Identificación fiscal">
          <Row label="EIN" value={entity.ein || '—'} mono />
          <Row label="Status EIN" value={entity.ein_status} />
          <Row label="Emitido" value={entity.ein_issued_date ? formatDate(entity.ein_issued_date) : '—'} />
        </Section>

        <Section title="Estructura">
          <Row label="Tipo" value={entity.structure === 'single_member' ? 'Single-Member' : 'Multi-Member'} />
          <Row label="Clasificación fiscal" value={entity.tax_classification} />
          <Row label="Tipo de dueño" value={entity.owner_type} />
          <Row label="Dueño" value={entity.owner_name || '—'} />
          <Row label="País" value={entity.owner_country || '—'} mono />
          <Row label="FTIN" value={entity.owner_ftin || '—'} mono />
        </Section>

        <Section title="Registered Agent">
          <Row label="Proveedor" value={entity.registered_agent_provider || '—'} />
          <Row label="Nombre" value={entity.registered_agent_name || '—'} />
          <Row label="Renovación" value={entity.registered_agent_renewal_date ? formatDate(entity.registered_agent_renewal_date) : '—'} />
          <Row label="Costo anual" value={entity.registered_agent_annual_cost ? formatCurrency(entity.registered_agent_annual_cost) : '—'} />
        </Section>

        <Section title="Operación">
          <Row label="Modelo" value={entity.business_model || '—'} />
          <Row label="Mercados" value={entity.target_markets ? JSON.parse(entity.target_markets).join(', ') : '—'} />
          <Row label="Clientes US" value={entity.has_us_customers ? 'Sí' : 'No'} />
          <Row label="Empleados" value={entity.has_employees ? 'Sí' : 'No'} />
          <Row label="Contractors" value={entity.has_contractors ? 'Sí' : 'No'} />
        </Section>

        <Section title="Banca">
          <Row label="Proveedor" value={entity.bank_provider || '—'} />
          <Row label="Apertura" value={entity.bank_account_opened_date ? formatDate(entity.bank_account_opened_date) : '—'} />
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <h3 style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontWeight: 400, marginBottom: '1.5rem' }}>
        {title}
      </h3>
      <div className="stack-sm">{children}</div>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', padding: '0.5rem 0', borderBottom: '1px solid var(--border)' }}>
      <span className="eyebrow">{label}</span>
      <span className={mono ? 'mono' : ''} style={{ color: 'var(--text)', fontSize: '0.9rem', textAlign: 'right' }}>
        {value}
      </span>
    </div>
  );
}

// ================================================================
// Compliance (per jurisdiction)
// ================================================================

export function CompliancePage() {
  const { jurisdiction } = useParams();
  const [obligations, setObligations] = useState<any[] | null>(null);

  useEffect(() => {
    api.obligations.list({ jurisdiction }).then((r) => setObligations(r.obligations));
  }, [jurisdiction]);

  if (!obligations) return <Loading />;

  const pending = obligations.filter((o) => o.status !== 'completed' && o.status !== 'waived');
  const completed = obligations.filter((o) => o.status === 'completed');

  const title = jurisdiction === 'federal' ? 'Federal · IRS' : jurisdiction === 'wyoming' ? 'Wyoming' : jurisdictionLabel(jurisdiction || '');

  return (
    <div className="fade-up">
      <div className="page-header">
        <div className="page-eyebrow">Compliance</div>
        <h1 className="page-title"><em>{title}</em></h1>
      </div>

      {pending.length === 0 && completed.length === 0 ? (
        <Empty title="Sin obligaciones en esta jurisdicción" />
      ) : (
        <div className="stack-lg">
          {pending.length > 0 && (
            <section>
              <div className="eyebrow" style={{ marginBottom: '1rem' }}>Pendientes</div>
              <div className="stack-sm">
                {pending.map((ob) => (
                  <ComplianceItem key={ob.id} ob={ob} />
                ))}
              </div>
            </section>
          )}
          {completed.length > 0 && (
            <section>
              <div className="eyebrow" style={{ marginBottom: '1rem' }}>Completadas</div>
              <div className="stack-sm">
                {completed.map((ob) => (
                  <ComplianceItem key={ob.id} ob={ob} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function ComplianceItem({ ob }: { ob: any }) {
  const [completing, setCompleting] = useState(false);

  const handleComplete = async () => {
    if (!confirm(`¿Marcar "${ob.title}" como completada?`)) return;
    setCompleting(true);
    try {
      await api.obligations.complete(ob.id);
      window.location.reload();
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div
      className="card"
      style={{
        borderLeft: `3px solid ${
          ob.urgency === 'overdue' ? 'var(--danger-bright)'
          : ob.urgency === 'critical' ? 'var(--danger)'
          : ob.urgency === 'warning' ? 'var(--accent)'
          : ob.urgency === 'completed' ? 'var(--success)'
          : 'var(--border-strong)'
        }`,
      }}
    >
      <div className="flex-between" style={{ marginBottom: '1rem' }}>
        <div>
          <div className="eyebrow" style={{ marginBottom: '0.5rem' }}>
            {jurisdictionLabel(ob.jurisdiction)} · {ob.type}
          </div>
          <h3 style={{ fontSize: '1.15rem', fontWeight: 500 }}>{ob.title}</h3>
        </div>
        <StatusPill urgency={ob.urgency} label={urgencyLabel(ob.urgency)} />
      </div>

      {ob.description && (
        <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem', lineHeight: 1.7, marginBottom: '1rem' }}>
          {ob.description}
        </p>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '1rem',
          padding: '1rem 0',
          borderTop: '1px solid var(--border)',
          borderBottom: '1px solid var(--border)',
          margin: '1rem 0',
        }}
      >
        <Metric label="Vence" value={ob.due_date} urgency={ob.urgency} />
        <Metric label="Cuándo" value={formatDaysUntil(ob.days_until_due)} />
        <Metric label="Costo" value={`${formatCurrency(ob.cost_estimate_min)}–${formatCurrency(ob.cost_estimate_max)}`} />
        {ob.penalty_amount && (
          <Metric label="Multa" value={formatCurrency(ob.penalty_amount)} urgency="critical" />
        )}
      </div>

      {ob.penalty_description && (
        <p className="mono" style={{ fontSize: '0.75rem', color: 'var(--danger-bright)', lineHeight: 1.6, marginBottom: '1rem' }}>
          ⚠ {ob.penalty_description}
        </p>
      )}

      <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        {ob.learning_topic_key && (
          <Link to={`/learn/${ob.learning_topic_key}`} style={{ fontSize: '0.85rem' }}>
            Guía completa →
          </Link>
        )}
        {ob.status !== 'completed' && ob.status !== 'waived' && (
          <button
            onClick={handleComplete}
            disabled={completing}
            className="btn btn-ghost"
            style={{ fontSize: '0.8rem' }}
          >
            {completing ? 'Guardando…' : 'Marcar como completada'}
          </button>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, urgency }: { label: string; value: string; urgency?: string }) {
  return (
    <div>
      <div className="eyebrow" style={{ marginBottom: '0.3rem' }}>{label}</div>
      <div
        className="mono"
        style={{
          fontSize: '0.9rem',
          color:
            urgency === 'overdue' || urgency === 'critical' ? 'var(--danger-bright)'
            : urgency === 'warning' ? 'var(--accent)'
            : 'var(--text)',
        }}
      >
        {value}
      </div>
    </div>
  );
}

// ================================================================
// Transactions page
// ================================================================

export function TransactionsPage() {
  const [transactions, setTransactions] = useState<any[] | null>(null);
  const [entity, setEntity] = useState<any>(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    api.entities.list().then((r) => setEntity(r.entities[0] ?? null));
    loadTransactions();
  }, []);

  const loadTransactions = async () => {
    const r = await api.transactions.list();
    setTransactions(r.transactions);
  };

  if (!transactions || !entity) return <Loading />;

  return (
    <div className="fade-up">
      <div className="page-header">
        <div className="page-eyebrow">Form 5472 · Reportable</div>
        <h1 className="page-title"><em>Transacciones</em></h1>
        <p className="page-description">
          Registro continuo de transacciones entre ILU LLC y su foreign owner. Todo lo que se registre aquí alimenta el Form 5472 anual.
        </p>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancelar' : '+ Nueva transacción'}
        </button>
      </div>

      {showForm && (
        <TransactionForm
          entityId={entity.id}
          onCreated={() => {
            setShowForm(false);
            loadTransactions();
          }}
        />
      )}

      {transactions.length === 0 ? (
        <Empty title="Sin transacciones registradas" description="Cada aportación de capital, distribución, y movimiento con el owner debe registrarse aquí." />
      ) : (
        <div className="stack-sm">
          {transactions.map((tx) => (
            <div key={tx.id} className="card-raised" style={{ display: 'grid', gridTemplateColumns: '100px 1fr auto', gap: '1.5rem', alignItems: 'center' }}>
              <div className="mono" style={{ fontSize: '0.8rem' }}>{tx.date}</div>
              <div>
                <div style={{ marginBottom: '0.25rem' }}>{transactionTypeLabel(tx.transaction_type)}</div>
                <div className="mono" style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  → {tx.related_party_name} · tax year {tx.tax_year}
                  {tx.remittance_tax_applies ? ' · 1% remittance tax aplica' : ''}
                </div>
              </div>
              <div className="numeric" style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem' }}>
                {formatCurrency(tx.amount_usd)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TransactionForm({ entityId, onCreated }: { entityId: string; onCreated: () => void }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    amount: '',
    transaction_type: 'capital_contribution',
    related_party_name: '',
    related_party_country: 'MX',
    description: '',
    funding_source: 'bank_account',
  });
  const [evaluation, setEvaluation] = useState<any>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const res = await api.transactions.create({
      entity_id: entityId,
      date: form.date,
      amount: parseFloat(form.amount),
      amount_usd: parseFloat(form.amount),
      transaction_type: form.transaction_type,
      related_party_name: form.related_party_name,
      related_party_country: form.related_party_country,
      description: form.description,
      funding_source: form.funding_source,
    });
    setEvaluation(res.remittance_tax_evaluation);
    setTimeout(onCreated, 1500);
  };

  return (
    <form onSubmit={handleSubmit} className="card" style={{ marginBottom: '2rem' }}>
      <h3 style={{ marginBottom: '1.5rem', fontFamily: 'var(--font-display)', fontStyle: 'italic' }}>Nueva transacción</h3>
      <div className="stack-md">
        <div>
          <label>Fecha</label>
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
        </div>
        <div>
          <label>Tipo</label>
          <select value={form.transaction_type} onChange={(e) => setForm({ ...form, transaction_type: e.target.value })}>
            <option value="capital_contribution">Aportación de capital</option>
            <option value="distribution">Distribución al owner</option>
            <option value="loan_to_owner">Préstamo al owner</option>
            <option value="loan_from_owner">Préstamo del owner</option>
            <option value="service_payment_to_owner">Pago de servicios al owner</option>
            <option value="service_payment_from_owner">Pago de servicios del owner</option>
            <option value="other">Otra</option>
          </select>
        </div>
        <div>
          <label>Monto (USD)</label>
          <input type="number" step="0.01" className="mono" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
        </div>
        <div>
          <label>Parte relacionada</label>
          <input type="text" value={form.related_party_name} onChange={(e) => setForm({ ...form, related_party_name: e.target.value })} required />
        </div>
        <div>
          <label>País</label>
          <input type="text" className="mono" maxLength={2} value={form.related_party_country} onChange={(e) => setForm({ ...form, related_party_country: e.target.value })} />
        </div>
        <div>
          <label>Método de fondeo</label>
          <select value={form.funding_source} onChange={(e) => setForm({ ...form, funding_source: e.target.value })}>
            <option value="bank_account">Cuenta bancaria (exento de 1% remittance tax)</option>
            <option value="debit_credit_card">Tarjeta débito/crédito USA (exento)</option>
            <option value="crypto">Cryptocurrency (actualmente exento)</option>
            <option value="cash">Efectivo (SÍ aplica 1% remittance tax)</option>
            <option value="money_order">Money order (SÍ aplica)</option>
            <option value="cashiers_check">Cashier's check (SÍ aplica)</option>
          </select>
        </div>
        <div>
          <label>Descripción</label>
          <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <button type="submit" className="btn btn-primary">Registrar</button>
      </div>
      {evaluation && (
        <div style={{ marginTop: '1.5rem', padding: '1rem', border: '1px solid var(--accent)', fontSize: '0.85rem' }}>
          <div className="eyebrow" style={{ color: 'var(--accent)', marginBottom: '0.5rem' }}>Evaluación remittance tax</div>
          {evaluation.applies
            ? <>1% tax aplica: <strong>{formatCurrency(evaluation.amount)}</strong></>
            : <>Exento: {evaluation.exempt_reason}</>
          }
        </div>
      )}
    </form>
  );
}

// ================================================================
// Notices page
// ================================================================

export function NoticesPage() {
  const [notices, setNotices] = useState<any[] | null>(null);

  useEffect(() => {
    api.notices.list().then((r) => setNotices(r.notices));
  }, []);

  if (!notices) return <Loading />;

  return (
    <div className="fade-up">
      <div className="page-header">
        <div className="page-eyebrow">IRS · Wyoming SoS</div>
        <h1 className="page-title">Notices</h1>
        <p className="page-description">
          Correspondencia oficial recibida vía Registered Agent. Cada notice requiere respuesta dentro del plazo indicado.
        </p>
      </div>

      {notices.length === 0 ? (
        <Empty title="Sin notices registradas" description="Cuando recibas correspondencia del IRS o del estado, regístrala aquí para trackear respuestas." />
      ) : (
        <div className="stack-sm">
          {notices.map((n) => (
            <div key={n.id} className="card-raised">
              <div className="flex-between" style={{ marginBottom: '0.5rem' }}>
                <div className="mono" style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {n.source.toUpperCase()} · {n.notice_code || 'Sin código'} · {formatDate(n.received_date)}
                </div>
                <StatusPill urgency={n.priority === 'critical' ? 'critical' : 'warning'} label={n.status} />
              </div>
              <h4>{n.subject || 'Sin asunto'}</h4>
              {n.summary && <p style={{ marginTop: '0.5rem', color: 'var(--text-dim)', fontSize: '0.9rem' }}>{n.summary}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ================================================================
// Documents page
// ================================================================

export function DocumentsPage() {
  const [documents, setDocuments] = useState<any[] | null>(null);

  useEffect(() => {
    api.documents.list().then((r) => setDocuments(r.documents));
  }, []);

  if (!documents) return <Loading />;

  return (
    <div className="fade-up">
      <div className="page-header">
        <div className="page-eyebrow">R2 Storage</div>
        <h1 className="page-title">Documentos</h1>
      </div>

      {documents.length === 0 ? (
        <Empty title="Sin documentos" description="Sube tu Operating Agreement, CP 575 (EIN confirmation), Certificate of Formation, y cualquier notice recibida." />
      ) : (
        <div className="stack-sm">
          {documents.map((d) => (
            <div key={d.id} className="card-raised flex-between">
              <div>
                <div>{d.title}</div>
                <div className="mono" style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {d.kind} · {d.mime_type} · {Math.round((d.size_bytes ?? 0) / 1024)} KB
                </div>
              </div>
              <a href={api.documents.downloadUrl(d.id)} target="_blank" rel="noopener noreferrer" className="btn btn-ghost">
                Descargar
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ================================================================
// Contracts page
// ================================================================

export function ContractsPage() {
  const [contracts, setContracts] = useState<any[] | null>(null);

  useEffect(() => {
    api.contracts.list().then((r) => setContracts(r.contracts));
  }, []);

  if (!contracts) return <Loading />;

  return (
    <div className="fade-up">
      <div className="page-header">
        <div className="page-eyebrow">Legal</div>
        <h1 className="page-title">Contratos</h1>
      </div>

      {contracts.length === 0 ? (
        <Empty title="Sin contratos registrados" description="Registra tu Operating Agreement, Terms of Service, Privacy Policy, MSAs con clientes, y contratos con vendors." />
      ) : (
        <div className="stack-sm">
          {contracts.map((c) => (
            <div key={c.id} className="card-raised flex-between">
              <div>
                <div>{c.title}</div>
                <div className="mono" style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {c.kind} · {c.counterparty || 'Sin counterparty'} · {c.status}
                </div>
              </div>
              {c.renewal_date && (
                <div className="mono" style={{ fontSize: '0.85rem' }}>
                  Renov: {c.renewal_date}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ================================================================
// Learning Library
// ================================================================

export function LearnIndexPage() {
  const [topics, setTopics] = useState<any[] | null>(null);

  useEffect(() => {
    api.learning.list().then((r) => setTopics(r.topics));
  }, []);

  if (!topics) return <Loading />;

  const byCategory: Record<string, any[]> = {};
  topics.forEach((t) => {
    if (!byCategory[t.category]) byCategory[t.category] = [];
    byCategory[t.category].push(t);
  });

  return (
    <div className="fade-up">
      <div className="page-header">
        <div className="page-eyebrow">Aprendizaje</div>
        <h1 className="page-title"><em>Biblioteca</em></h1>
        <p className="page-description">
          Guías detalladas de cada obligación. Qué es, por qué aplica, cuánto cuesta, qué pasa si no se cumple, y cómo hacerla paso a paso.
        </p>
      </div>

      <div className="stack-lg">
        {Object.entries(byCategory).map(([cat, catTopics]) => (
          <section key={cat}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontWeight: 400, marginBottom: '1.5rem', textTransform: 'capitalize' }}>
              {jurisdictionLabel(cat)}
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
              {catTopics.map((t) => (
                <Link
                  key={t.topic_key}
                  to={`/learn/${t.topic_key}`}
                  className="card-raised"
                  style={{ textDecoration: 'none', borderBottom: 'none' }}
                >
                  <div className="eyebrow" style={{ marginBottom: '0.5rem' }}>{t.topic_key}</div>
                  <h4 style={{ marginBottom: '0.5rem' }}>{t.title}</h4>
                  <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem', lineHeight: 1.6 }}>{t.summary}</p>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

export function LearnTopicPage() {
  const { topic_key } = useParams();
  if (!topic_key) return <Empty title="Tema no encontrado" />;
  return (
    <div className="fade-up">
      <div style={{ marginBottom: '2rem' }}>
        <Link to="/learn" style={{ fontSize: '0.85rem' }}>← Volver a la biblioteca</Link>
      </div>
      <LearningCard topicKey={topic_key} />
    </div>
  );
}
